import React, { useMemo, forwardRef } from 'react';

import { useSelector } from 'react-redux';

import Heading from '@atlaskit/heading';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { CommentIcon, IconSizes } from '@atlassian/bitkit-icon';

import { DiffStatusIcon } from 'src/components/diff-file-status/diff-file-status-icon-mapper';
import { FileClickProps } from 'src/components/file-tree';
import { useIntl } from 'src/hooks/intl';
import { getGlobalIsColorBlindModeEnabled } from 'src/redux/diff-settings';
import { getActiveDiff } from 'src/redux/pull-request/selectors';
import { useSingleDiffReviewed } from 'src/sections/repository/sections/pull-request/components/reviewed-files/reviewed-files-provider';
import { DiffStatStatus } from 'src/types/diffstat';

import { ScreenReadersOnly } from '../../accessibility';
import messages from '../../file-tree/src/i18n';

import * as styles from './styled';

export type FileProps = {
  isHidden?: boolean;
  name: string;
  href: string;
  fileDiffStatus?: DiffStatStatus;
  isConflicted?: boolean;
  comments?: number;
  linesAdded?: number;
  linesRemoved?: number;
  onClick?: (event: React.MouseEvent, props: FileClickProps) => void;
};

export const FileListFile = ({
  isHidden = false,
  name,
  href,
  comments = 0,
  linesAdded = 0,
  linesRemoved = 0,
  fileDiffStatus,
  isConflicted,
  onClick = () => {},
}: FileProps) => {
  const intl = useIntl();

  const activeDiff = useSelector(getActiveDiff);
  const isColorBlindModeEnabled = useSelector(getGlobalIsColorBlindModeEnabled);
  const isActive = useMemo(
    () => !!activeDiff && `#${activeDiff}` === href,
    [activeDiff, href]
  );

  const filepath =
    typeof href === 'string' ? href.split('#chg-')[1] || null : null;
  const isReviewed = useSingleDiffReviewed(filepath);

  const COMMENT_COLOR = token('color.text', colors.N800);

  const clickHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onClick(event, {
      name,
      href,
      isHidden,
      fileDiffStatus,
      isConflicted,
      comments: null,
    });
  };

  const renderComments = () => {
    const commentsMessage = intl.formatMessage(messages.comment, {
      count: comments,
    });
    return (
      <styles.CommentsWrapper data-testid="file-list-file__comments">
        <CommentIcon
          primaryColor={COMMENT_COLOR}
          size={IconSizes.Small}
          label=""
        />
        <styles.CommentsNumber>
          <Heading level="h200" as="span">
            {comments}
          </Heading>
          <ScreenReadersOnly>{commentsMessage}</ScreenReadersOnly>
        </styles.CommentsNumber>
      </styles.CommentsWrapper>
    );
  };

  const tag = forwardRef<HTMLSpanElement>(({ ...props }, ref) => (
    <styles.TooltipWrapper ref={ref} {...props} />
  ));

  return (
    <styles.File
      isActive={isActive}
      isHidden={isHidden}
      href={href}
      onClick={clickHandler}
    >
      <DiffStatusIcon fileDiffStatus={fileDiffStatus} />
      <Tooltip
        position={isHidden ? 'bottom' : 'left'}
        content={isHidden ? intl.formatMessage(messages.hiddenTooltip) : name}
        tag={tag}
      >
        <styles.FileName isReviewed={isReviewed}>
          <Heading level="h200" as="span">
            {name}
          </Heading>
        </styles.FileName>
      </Tooltip>
      <styles.FileNotes>
        <>
          {comments > 0 && renderComments()}
          {isConflicted ? (
            <WarningIcon
              primaryColor={token('color.icon.warning', colors.Y300)}
              label={intl.formatMessage(messages.conflicted)}
            />
          ) : (
            <>
              {linesAdded > 0 && (
                <styles.LinesAdded
                  isColorBlindMode={isColorBlindModeEnabled}
                  cutCorners={linesRemoved > 0}
                >
                  <Heading level="h200" as="span">
                    +{linesAdded}
                  </Heading>
                </styles.LinesAdded>
              )}
              {linesRemoved > 0 && (
                <styles.LinesRemoved
                  isColorBlindMode={isColorBlindModeEnabled}
                  cutCorners={linesAdded > 0}
                >
                  <Heading level="h200" as="span">
                    -{linesRemoved}
                  </Heading>
                </styles.LinesRemoved>
              )}
            </>
          )}
        </>
      </styles.FileNotes>
    </styles.File>
  );
};
